import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { DATE_FORMAT_DASHED, TIME_INTERVALS } from '@utils/dates/dateMethods';
import {
    addToDate,
    endOf,
    getDateObject,
    getDateTimeInMillisecond,
    isDateBetween,
    startOf,
    subtractFromDate,
} from '../dates/date.utils';

const checkRegex = (value, regex, affordableValues = undefined, reverse = undefined) => {
    if (affordableValues && affordableValues.includes(value)) {
        return true;
    } else {
        if (value === null) {
            return false;
        } else {
            return reverse ? !(value && value.match(regex)) : !!(value && value.match(regex));
        }
    }
};

export const trimValue = value => {
    let trimmedValue = value;

    if (typeof value === 'string' || value instanceof String) {
        trimmedValue = value.trim();
    }

    return trimmedValue;
};

const methods = {
    address: ([value]) => checkRegex(trimValue(value), /^[A-Za-z0-9-.# ]*$/),

    addressOrEmpty: ([value]) => checkRegex(value, /^[A-Za-z0-9-.# ]*$/, ['', null]),

    adjustTime: ([value]) => !!getDateTimeInMillisecond(value),

    alphanumeric: ([value]) => checkRegex(value, /^[A-Za-z0-9]*$/),

    alienNumber: ([value, isNJ]) => {
        //Looks like this method has to be refactored. TODO: do refactoring once there is bandwidth

        if (isNJ) {
            return value === null || value === '' || checkRegex(String(value), /^\d{9}$/);
        }

        switch (true) {
            case value === null:
            case value === '':
                return true;
            case checkRegex(String(value), /^(\d)\1{8}$/):
            case checkRegex(String(value), /^123456789$/):
            case !checkRegex(String(value), /^\d{9}$/):
                return false;
            default:
                return true;
        }
    },

    certificateOrNaturalizationNumber: ([value]) =>
        value === null || value === '' || checkRegex(String(value), /^\d{6,12}$/),

    checked: ([value]) => !!value,

    checkIfUnique: ([value, setOfUniques]) => !setOfUniques?.has(value),

    city: ([value]) => checkRegex(value, /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/),

    cityOrEmpty: ([value]) => checkRegex(value, /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/, ['', null]),

    dateBetweenTwoDates: ([value, [priorDate, futureDate]]) => {
        return isDateBetween(value, priorDate, futureDate, 'days', '[]');
    },

    dateGreaterThanStartDate: ([value, startDate]) => {
        switch (startDate) {
            case null:
                return true;
            default:
                return getDateTimeInMillisecond(value) > getDateTimeInMillisecond(startDate);
        }
    },

    dateNotGreaterThanCurrentDate: ([value, serverDate]) => {
        return getDateTimeInMillisecond(value) <= getDateTimeInMillisecond(serverDate);
    },

    dateWithinLastNDays: ([value, [serverDate, range]]) => {
        if (value === null) {
            return true;
        }
        if (value === DATE_FORMAT_DASHED) {
            return false;
        }

        const args = range === TIME_INTERVALS.ONE_YEAR ? [1, 'years'] : [range + 1, 'days'];

        const currentDate = endOf(serverDate, 'day');
        const priorDate = subtractFromDate(...args, endOf(serverDate, 'day'));

        return isDateBetween(value, priorDate, currentDate);
    },

    dateNotLessThanCHIPDenialDate: ([value, serverDate]) => {
        const adjustedServerDate = startOf(serverDate, 'day');
        const adjustedMedicaidChipDenialDate = startOf(value, 'day');

        return (
            adjustedMedicaidChipDenialDate.valueOf() <= adjustedServerDate.valueOf() &&
            getDateTimeInMillisecond(value) > subtractFromDate(91, 'days', serverDate)
        );
    },

    dateMinimumThreshold: ([value]) => getDateTimeInMillisecond(value) >= getDateTimeInMillisecond('1900-01-01'),

    datepicker: ([value]) => value && checkRegex(value, /^[0-9]+$/),

    datePregnancy: ([value, serverDate]) => 
        getDateObject(value).getTime() > subtractFromDate(61, 'days', serverDate) && 
        getDateObject(value).getTime() < addToDate(280, 'days', serverDate),

    dateNotNull: ([value]) => value !== null,

    digits: ([value]) => checkRegex(String(value), /^[0-9]*$/),

    digitsOrEmpty: ([value]) => checkRegex(value, /^[0-9]*$/, ['', null]),

     
    email: ([value]) =>
        checkRegex(
            value,
             
            /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/,
        ),

    employerName: ([value]) => checkRegex(value, /^[a-zA-Z 0-9~!#$^&*()\-:',.]*$/),

    exactLength: ([value, , length]) => value && value.length === length,

    exactMatch: ([value, valuesToMatch]) =>
        valuesToMatch.some(valueToMatch => valueToMatch.toLowerCase() === value.toLowerCase()),

    greenCard: ([value]) => checkRegex(value, /^[a-zA-Z]{3}[0-9]/),

    length: ([value, [minLength, maxLength]]) => value.length >= minLength && value.length <= maxLength,

    maxLength: ([value, , length]) => (value === null || value === '' ? true : String(value).length <= length),

    middleName: ([value]) => checkRegex(value, /^[a-zA-Z -.']+$/, ['', null]),

    minLength: ([value, , length]) => (value === null || value === '' ? true : String(value).length >= length),

    noLetters: ([value]) => checkRegex(value, /[a-zA-Z]/, [null], true),

    notEmpty: args => {
        const [value, valueToMatch = undefined] = args;

        if (valueToMatch) {
            return value !== valueToMatch;
        }
        return value !== '' && value !== null;
    },

    notUnspecified: ([value]) => value !== 'UNSPECIFIED',

    notZeroAndNotEmpty: ([value]) => value !== 0 && value !== '' && value !== null,

    personName: ([value]) => checkRegex(value, /^[a-z]+(?:['-.\s][a-z]+)*$/i),

    phoneNumber: ([value]) => {
        if (value === null || value === '') {
            return true;
        } else {
            const numbersQty =
                typeof value === 'string' &&
                value
                    .split('')
                    .filter(c => c.match(/^[0-9]*$/))
                    .join('').length;
            return numbersQty === 10;
        }
    },

    range: ([value, rangeToMatch]) => {
        // For negative income sources amounts: eligible only tribal income is equal to 0
        if (rangeToMatch.length === 0) {
            return value === 0;
        }
        // Common case
        return +value >= rangeToMatch[0] && value <= rangeToMatch[1];
    },

    ssn: ([value]) =>
        checkRegex(
            value,
            /^([1-57-8][0-9]{2}|0([1-9][0-9]|[0-9][1-9])|6([0-57-9][0-9]|[0-9][0-57-9]))([1-9][0-9]|[0-9][1-9])([1-9]\d{3}|\d[1-9]\d{2}|\d{2}[1-9]\d|\d{3}[1-9])$/,
        ),

    timeOrNull: ([value]) => value !== DATE_FORMAT_DASHED,

    timeOrNullGreaterThanStartDate: ([value, startDate]) => {
        if (value === DATE_FORMAT_DASHED) {
            return false;
        } else if (value === null) {
            return true;
        } else {
            switch (startDate) {
                case null:
                case DATE_FORMAT_DASHED:
                    return true;
                default:
                    return getDateTimeInMillisecond(value) > getDateTimeInMillisecond(startDate);
            }
        }
    },

    checkIfSpecified: ([value]) => value !== 'UNSPECIFIED',

    zipCode: ([value]) => checkRegex(value, /^\d{5}(-\d{4})?$/),

    zipCode5Digits: ([value]) => checkRegex(value, /^\d{5}?$/),
};

const VALIDATION_CONFIG = [
    {
        validatorName: VALIDATION_TYPES.ADDRESS_LINE1,
        payload: {
            methods: ['address', ['minLength', 1], ['maxLength', 55]],
            messages: [VALIDATION_ERROR_MESSAGES.ADDRESS],
        },
    },
    {
        validatorName: VALIDATION_TYPES.ADDRESS_LINE2,
        payload: {
            methods: ['addressOrEmpty', ['minLength', 1], ['maxLength', 55]],
            messages: [VALIDATION_ERROR_MESSAGES.ADDRESS],
        },
    },
    {
        validatorName: VALIDATION_TYPES.ADDRESS_LINE_OPTIONAL,
        payload: {
            methods: ['addressOrEmpty', ['minLength', 1], ['maxLength', 55]],
            messages: [VALIDATION_ERROR_MESSAGES.ADDRESS],
        },
    },
    {
        validatorName: VALIDATION_TYPES.ALIEN_NUMBER,
        payload: {
            methods: ['alienNumber'],
            messages: [VALIDATION_ERROR_MESSAGES.ALIEN_NUMBER],
        },
    },
    {
        validatorName: VALIDATION_TYPES.ALL_DIGITS,
        payload: {
            methods: ['digits', ['minLength', 1], ['maxLength', 2]],
            messages: [VALIDATION_ERROR_MESSAGES.RANGE_UP_TO_19],
        },
    },
    {
        validatorName: VALIDATION_TYPES.CERTIFICATE_NUMBER,
        payload: {
            methods: ['certificateOrNaturalizationNumber'],
            messages: [VALIDATION_ERROR_MESSAGES.CERTIFICATE_NUMBER],
        },
    },
    {
        validatorName: VALIDATION_TYPES.CHECKED,
        payload: {
            methods: ['checked'],
            messages: [VALIDATION_ERROR_MESSAGES.CHECKED],
        },
    },
    {
        validatorName: VALIDATION_TYPES.CHOSEN,
        payload: {
            methods: ['notEmpty'],
            messages: [VALIDATION_ERROR_MESSAGES.CHOSEN],
        },
    },
    {
        validatorName: VALIDATION_TYPES.CITY,
        payload: {
            methods: ['city', ['minLength', 2], ['maxLength', 30]],
            messages: [VALIDATION_ERROR_MESSAGES.CITY],
        },
    },
    {
        validatorName: VALIDATION_TYPES.CITY_OPTIONAL,
        payload: {
            methods: ['cityOrEmpty', ['minLength', 2], ['maxLength', 30]],
            messages: [VALIDATION_ERROR_MESSAGES.CITY],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE,
        payload: {
            methods: ['adjustTime', 'dateMinimumThreshold', 'dateNotNull'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_OR_NULL,
        payload: {
            methods: ['timeOrNull'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_OR_NULL_GREATER_THAN_START,
        payload: {
            methods: ['timeOrNullGreaterThanStartDate'],
            messages: [VALIDATION_ERROR_MESSAGES.INVALID_DATE_OR_NOT_AFTER_START],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
        payload: {
            methods: ['dateBetweenTwoDates'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_GREATER_THAN_START,
        payload: {
            methods: ['adjustTime', 'dateMinimumThreshold', 'dateNotNull', 'dateGreaterThanStartDate'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE_NOT_AFTER_START],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_NOT_GREATER_THAN_CURRENT,
        payload: {
            methods: ['adjustTime', 'dateNotGreaterThanCurrentDate', 'dateMinimumThreshold', 'dateNotNull'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATEPICKER,
        payload: {
            methods: ['datepicker'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_PREGNANCY,
        payload: {
            methods: ['adjustTime', 'datePregnancy', 'dateNotNull'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DATE_VALID_CHIP_DENIAL_DATE,
        payload: {
            methods: ['adjustTime', 'dateNotLessThanCHIPDenialDate', 'dateMinimumThreshold', 'dateNotNull'],
            messages: [VALIDATION_ERROR_MESSAGES.CHIP_DENIAL],
        },
    },
    {
        validatorName: VALIDATION_TYPES.EMAIL,
        payload: {
            methods: ['email', ['maxLength', 100]],
            messages: [VALIDATION_ERROR_MESSAGES.EMAIL],
        },
    },
    {
        validatorName: VALIDATION_TYPES.EMPLOYER_NAME,
        payload: {
            methods: ['employerName', ['minLength', 2], ['maxLength', 80]],
            messages: [VALIDATION_ERROR_MESSAGES.EMPLOYER_NAME],
        },
    },
    {
        validatorName: VALIDATION_TYPES.I_551,
        payload: {
            methods: ['greenCard', ['exactLength', 13]],
            messages: [VALIDATION_ERROR_MESSAGES.I_551],
        },
    },
    {
        validatorName: VALIDATION_TYPES.I_94,
        payload: {
            methods: ['alphanumeric', ['exactLength', 11]],
            messages: [VALIDATION_ERROR_MESSAGES.I_94],
        },
    },
    {
        validatorName: VALIDATION_TYPES.IS_SIGNED,
        payload: {
            methods: ['exactMatch'],
            messages: [VALIDATION_ERROR_MESSAGES.SIGNATURE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.LAST_N_DAYS,
        payload: {
            methods: ['dateWithinLastNDays'],
            messages: [VALIDATION_ERROR_MESSAGES.DATE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.LENGTH,
        payload: {
            methods: ['length'],
            messages: [VALIDATION_ERROR_MESSAGES.LENGTH],
        },
    },
    {
        validatorName: VALIDATION_TYPES.NAME,
        payload: {
            methods: ['personName', ['minLength', 1], ['maxLength', 60]],
            messages: [VALIDATION_ERROR_MESSAGES.NAME],
        },
    },
    {
        validatorName: VALIDATION_TYPES.MIDDLE_NAME,
        payload: {
            methods: ['middleName', ['minLength', 1], ['maxLength', 60]],
            messages: [VALIDATION_ERROR_MESSAGES.NAME],
        },
    },
    {
        validatorName: VALIDATION_TYPES.MONEY,
        payload: {
            methods: ['notZeroAndNotEmpty'],
            messages: [VALIDATION_ERROR_MESSAGES.MONEY],
        },
    },
    {
        validatorName: VALIDATION_TYPES.MONEY_NOT_EXCEED,
        payload: {
            methods: ['range'],
            messages: [VALIDATION_ERROR_MESSAGES.AMOUNT_LESS],
        },
    },
    {
        validatorName: VALIDATION_TYPES.NATURALIZATION_NUMBER,
        payload: {
            methods: ['alphanumeric', ['minLength', 6], ['maxLength', 12]],
            messages: [VALIDATION_ERROR_MESSAGES.NATURALIZATION_NUMBER],
        },
    },
    {
        validatorName: VALIDATION_TYPES.NOT_EMPTY,
        payload: {
            methods: ['notEmpty'],
            messages: [VALIDATION_ERROR_MESSAGES.NOT_EMPTY],
        },
    },
    {
        validatorName: VALIDATION_TYPES.NOT_UNSPECIFIED,
        payload: {
            methods: ['notUnspecified'],
            messages: [VALIDATION_ERROR_MESSAGES.CHOSEN],
        },
    },
    {
        validatorName: VALIDATION_TYPES.OTHER_RACE_OR_ETHNICITY,
        payload: {
            methods: [
                ['minLength', 5],
                ['maxLength', 256],
            ],
            messages: [VALIDATION_ERROR_MESSAGES.OTHER_RACE_OR_ETHNICITY],
        },
    },
    {
        validatorName: VALIDATION_TYPES.PASSPORT,
        payload: {
            methods: ['alphanumeric', ['minLength', 6], ['maxLength', 12]],
            messages: [VALIDATION_ERROR_MESSAGES.PASSPORT],
        },
    },
    {
        validatorName: VALIDATION_TYPES.PHONE_EXTENSION,
        payload: {
            methods: ['digitsOrEmpty', ['maxLength', 4]],
            messages: [VALIDATION_ERROR_MESSAGES.PHONE_EXTENSION],
        },
    },
    {
        validatorName: VALIDATION_TYPES.PHONE_NUMBER,
        payload: {
            methods: ['phoneNumber', 'noLetters', ['maxLength', 14]],
            messages: [VALIDATION_ERROR_MESSAGES.PHONE_NUMBER],
        },
    },
    {
        validatorName: VALIDATION_TYPES.PHONE_NUMBER_PROVIDED,
        payload: {
            methods: ['notEmpty', 'phoneNumber', 'noLetters', ['maxLength', 14]],
            messages: [VALIDATION_ERROR_MESSAGES.PHONE_NUMBER],
        },
    },
    {
        validatorName: VALIDATION_TYPES.RANGE,
        payload: {
            methods: ['range'],
            messages: [VALIDATION_ERROR_MESSAGES.RANGE],
        },
    },
    {
        validatorName: VALIDATION_TYPES.REPRESENTATIVE_ORGANIZATION_NAME,
        payload: {
            methods: [
                ['minLength', 1],
                ['maxLength', 250],
            ],
            messages: [VALIDATION_ERROR_MESSAGES.REPRESENTATIVE_ORGANIZATION_NAME],
        },
    },
    {
        validatorName: VALIDATION_TYPES.REPRESENTATIVE_ORGANIZATION_ID,
        payload: {
            methods: [
                ['minLength', 1],
                ['maxLength', 20],
            ],
            messages: [VALIDATION_ERROR_MESSAGES.REPRESENTATIVE_ORGANIZATION_ID],
        },
    },
    {
        validatorName: VALIDATION_TYPES.SEVIS_ID,
        payload: {
            methods: ['digits', ['exactLength', 10]],
            messages: [VALIDATION_ERROR_MESSAGES.SEVIS_ID],
        },
    },
    {
        validatorName: VALIDATION_TYPES.SPECIFIED,
        payload: {
            methods: ['checkIfSpecified'],
            messages: [VALIDATION_ERROR_MESSAGES.SPECIFIED],
        },
    },
    {
        validatorName: VALIDATION_TYPES.SSN,
        payload: {
            methods: ['ssn', ['exactLength', 9]],
            messages: [VALIDATION_ERROR_MESSAGES.SSN],
        },
    },
    {
        validatorName: VALIDATION_TYPES.VISA,
        payload: {
            methods: ['alphanumeric', ['exactLength', 8]],
            messages: [VALIDATION_ERROR_MESSAGES.VISA],
        },
    },
    {
        validatorName: VALIDATION_TYPES.UNIQUE,
        payload: {
            methods: ['checkIfUnique'],
            messages: [VALIDATION_ERROR_MESSAGES.UNIQUE_SSN],
        },
    },
    {
        validatorName: VALIDATION_TYPES.ZIP_CODE,
        payload: {
            methods: ['zipCode'],
            messages: [VALIDATION_ERROR_MESSAGES.ZIP],
        },
    },
    {
        validatorName: VALIDATION_TYPES.ZIP_CODE_5_DIGITS,
        payload: {
            methods: ['zipCode5Digits'],
            messages: [VALIDATION_ERROR_MESSAGES.ZIP_5_DIGITS],
        },
    },
    {
        validatorName: VALIDATION_TYPES.CHOSEN_FROM_LIST,
        payload: {
            methods: ['exactMatch'],
            messages: [VALIDATION_ERROR_MESSAGES.CHOSEN],
        },
    },
    {
        validatorName: VALIDATION_TYPES.DOCUMENT_DESCRIPTION,
        payload: {
            methods: [
                ['minLength', 1],
                ['maxLength', 35],
            ],
            messages: [VALIDATION_ERROR_MESSAGES.DOC_DESCRIPTION],
        },
    },
];

export const configuredValidator = (values, type) => {
    return VALIDATION_CONFIG.find(validator => validator.validatorName === type).payload.methods.map(
        methodDescription => {
            if (Array.isArray(methodDescription)) {
                // Checking if method has to be run with arguments
                const [method, ...args] = methodDescription;
                return methods[method]([...values, ...args]); // Yes. We pass values for validation and arguments
            } else {
                return methods[methodDescription]([...values]); // No. We pass only values
            }
        },
    );
};

const validators =  VALIDATION_CONFIG.map(type => ({
    validatorType: VALIDATION_TYPES[type.validatorName],
    verifier: ({ value, valuesToMatch, isGenShown }) => {
        const result = [...configuredValidator([value, valuesToMatch], VALIDATION_TYPES[type.validatorName])];

        isGenShown && console.log(`${type.validatorName} validation result:`, result);

        return result.every(check => check);
    },
    messages: type.payload.messages,
}));

const validateValueByValidationType = (value, validationType, valuesToMatch = undefined, isGenShown = false) => {
    const validator = validators.find(validator => validator.validatorType === validationType);

    return validator.verifier({ value, valuesToMatch, isGenShown });
};

export { validateValueByValidationType };

export default validators;
